'use client';

import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import { trackFormSubmitSuccess } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getDefaultUrlForTosOrGdprLocalized } from '@eliadmin/core/src/utils/misc-utils';
import { useMutation } from '@tanstack/react-query';
import Link from 'next/link';
import React from 'react';
import { RiSendPlane2Line } from 'react-icons/ri';

export function Newsletter() {
  const systemLabels = useSystemLabels();
  const { t } = useTranslations();
  const generalLayout = useGlobalStore(s => s.generalLayout);

  const isDesktop = useBreakpoint('sm');

  // state
  const [email, setEmail] = React.useState('');
  const [checked, setChecked] = React.useState(true);
  const [isSubscribed, setIsSubscribed] = React.useState(false);
  const [isAlreadySubscribed, setIsAlreadySubscribed] = React.useState(false);

  const subscribeMutation = useMutation({
    mutationFn: async (
      input: Parameters<
        (typeof import('@eliadmin/core/src/api/newsletter/newsletter-api'))['newsletterSubscribe']
      >[0],
    ) => {
      const { newsletterSubscribe } =
        await import('@eliadmin/core/src/api/newsletter/newsletter-api');
      return newsletterSubscribe(input);
    },
    onSuccess: () => {
      setIsSubscribed(true);
      trackFormSubmitSuccess({
        form_id: 'newsletter',
        form_name: 'newsletter_form',
        form_destination: typeof window !== 'undefined' ? window.location.href : undefined,
        form_length: 2,
        send_to: generalLayout.marketing?.gtm_id,
      });
    },
    onError: () => setIsAlreadySubscribed(true),
  });

  const locale = useLocaleStore(s => s.currentLocale);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!checked || isSubscribed || !email || subscribeMutation.isPending) return;
    subscribeMutation.mutate({
      locale,
      body: {
        email,
        gdpr: checked,
        return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
      },
    });
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'flex w-full flex-col items-start justify-start gap-4 rounded-[20px] bg-white p-6',
        'ea-newsletter',
      )}
    >
      <div className="flex w-full flex-col items-start justify-start gap-2">
        <Heading tag="h2" className={{ title: 'h4' }} title="Newsletter" />
        <Paragraph size="sm" color="black" className="w-full">
          {t('text3675')}
        </Paragraph>
      </div>
      <div className="flex w-full items-center justify-between gap-2">
        <Input
          aria-label={systemLabels('email')}
          id="newsletter-email"
          type="email"
          name="email"
          placeholder={t('text183')}
          onChange={e => setEmail(e.target.value)}
          className={{
            wrapper: cn(
              'w-full rounded-[20px] border',
              'ea-homepage-newsletter__form-item-email-input',
            ),
            main: cn('w-full rounded-[20px] px-3'),
          }}
        />
        <Button
          aria-label={t('text182')}
          type="submit"
          size={isDesktop ? 'default' : 'icon'}
          className={cn(
            ' w-fit whitespace-nowrap',
            'ea-homepage-newsletter__form-item-email-submit-button',
          )}
          disabled={email.length === 0 || !checked || isSubscribed}
          isLoading={subscribeMutation.isPending}
        >
          <p className="hidden sm:block">{t('text182')}</p>
          <RiSendPlane2Line size={20} className="block text-white sm:hidden" />
        </Button>
      </div>
      <p className="w-full text-xs tracking-tight text-black sm:text-sm">
        {t('text3753')}{' '}
        <Link
          href={getDefaultUrlForTosOrGdprLocalized(ROUTES.GDPR(), locale, 'gdpr')}
          className="underline"
          target="_blank"
        >
          {t('text614')}
        </Link>{' '}
        {t('text3389')}
      </p>
    </form>
  );
}
